import React from 'react';
import { Case } from "@/types/workflow";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronDown, ChevronUp, Edit2, FileText, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { nb } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface CaseMobileCardProps {
  caseData: Case;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onViewWorkflow: () => void;
}

export function CaseMobileCard({
  caseData,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onViewWorkflow,
}: CaseMobileCardProps) {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'completed':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
      className="w-full px-2 sm:px-0"
    >
      <Card className="bg-white/80 dark:bg-card/80 backdrop-blur-sm rounded-lg border shadow-sm overflow-hidden">
        <div className="p-3 space-y-3">
          <div className="flex justify-between items-start gap-2">
            <div className="space-y-2 flex-1 min-w-0">
              <h3 className="font-medium text-sm line-clamp-2 break-words">{caseData.title}</h3>
              <div className="flex flex-wrap gap-2 items-center">
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap",
                  getStatusColor(caseData.status)
                )}>
                  {caseData.status}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDistanceToNow(new Date(caseData.created_at), {
                    addSuffix: true,
                    locale: nb,
                  })}
                </span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 flex-shrink-0 hover:bg-transparent"
              onClick={onToggle}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Skjul detaljer" : "Vis detaljer"}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>

          <AnimatePresence>
            {isExpanded && caseData.description && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="bg-muted/50 rounded-md p-2 text-xs text-muted-foreground">
                  <p className="whitespace-pre-wrap break-words">
                    {caseData.description}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="grid grid-cols-3 gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={onViewWorkflow}
              className={cn(
                "w-full transition-all duration-200 text-xs h-8",
                "bg-white dark:bg-background",
                "hover:bg-primary/5 active:scale-95",
                "border-amber-200 dark:border-amber-900/50"
              )}
            >
              <FileText className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">Vis</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
              className={cn(
                "w-full transition-all duration-200 text-xs h-8",
                "bg-white dark:bg-background",
                "hover:bg-primary/5 active:scale-95",
                "border-amber-200 dark:border-amber-900/50"
              )}
            >
              <Edit2 className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">Rediger</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              className={cn(
                "w-full transition-all duration-200 text-xs h-8",
                "bg-white dark:bg-background",
                "hover:bg-destructive/10 active:scale-95",
                "border-destructive/50 text-destructive"
              )}
            >
              <Trash2 className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">Slett</span>
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}